import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Spinner } from './Spinner';
import { cn } from '../utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center font-medium',
    'transition-all duration-150 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'focus-visible:ring-[var(--color-brand)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:translate-y-0',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-[var(--color-brand)] text-[var(--color-brand-fg)]',
          'hover:bg-[var(--color-brand-hover)] hover:-translate-y-px hover:shadow-md',
          'shadow-sm',
        ],
        secondary: [
          'bg-[var(--color-bg-secondary)] text-[var(--color-fg-primary)]',
          'border border-[var(--color-border-default)]',
          'hover:bg-[var(--color-bg-tertiary)] hover:-translate-y-px hover:shadow-sm',
        ],
        ghost: [
          'bg-transparent text-[var(--color-fg-primary)]',
          'hover:bg-[var(--color-bg-secondary)]',
        ],
        danger: [
          'bg-[var(--color-danger)] text-white',
          'hover:bg-[hsl(0_84%_52%)] hover:-translate-y-px hover:shadow-md',
          'shadow-sm',
        ],
      },
      size: {
        sm: 'h-8 px-3 text-sm rounded-md gap-1.5',
        md: 'h-10 px-4 text-base rounded-md gap-2',
        lg: 'h-12 px-6 text-lg rounded-lg gap-2.5',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, leftIcon, rightIcon, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      data-loading={loading}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && <Spinner size="sm" />}
      {!loading && leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  ),
);
Button.displayName = 'Button';
